import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Users, Trophy, Instagram, ArrowUpRight, Heart, Camera, X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (metric, v) => (metric === "elevation" ? `${Math.round(v).toLocaleString()} m` : `${(v / 1000).toFixed(1)} km`);

export default function Community() {
  const [board, setBoard] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/challenges/gratitude-miles/leaderboard`)
      .then((r) => setBoard(r.data))
      .catch(() => {});
    axios
      .get(`${API}/photos`)
      .then((r) => setPhotos(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-16"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <Users className="w-4 h-4" /> The Tribe
          </p>
          <h1 data-testid="community-headline" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.06]">
            Runners make <span className="italic text-[#FF4E00]">runners.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed">
            12,000+ athletes across India and beyond — connected by start lines, Strava segments and
            Sunday long runs. Come as you are; leave faster.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] p-8"
            data-testid="community-leaderboard"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] flex items-center gap-2 mb-6">
              <Trophy className="w-4 h-4" /> Gratitude Miles — Live Leaders
            </p>
            {board ? (
              <ul className="space-y-4">
                {board.standings.slice(0, 5).map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <span className={`font-serif text-xl w-8 ${i === 0 ? "text-[#16A34A] font-semibold" : "text-stroke-amber"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm text-[#0D1117]">{s.name}</span>
                    </div>
                    <span className="font-serif text-lg text-[#FF4E00]">{fmt(board.challenge.metric, s.total)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Loading standings...</p>
            )}
            <Link
              to="/challenges"
              className="mt-7 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00] hover:gap-3 transition-all"
            >
              Join a challenge <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>

          <div className="space-y-6">
            <motion.a
              href="https://www.strava.com"
              target="_blank"
              rel="noopener"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              data-testid="community-strava-link"
              className="group flex items-center justify-between gap-6 rounded-3xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] p-8 transition-all duration-300 hover:-translate-y-1"
            >
              <div>
                <h2 className="font-serif text-xl sm:text-2xl text-[#0D1117] group-hover:text-[#FF4E00] transition-colors">Fitygrity on Strava</h2>
                <p className="text-sm text-[#374151] mt-2 leading-relaxed">
                  Follow the club, log your miles, and let verified efforts feed the arena leaderboards.
                </p>
              </div>
              <ArrowUpRight className="w-6 h-6 text-[#FF4E00] shrink-0 transition-transform group-hover:rotate-45" />
            </motion.a>
            <motion.a
              href="https://www.instagram.com/mukundnaair"
              target="_blank"
              rel="noopener"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              data-testid="community-instagram-link"
              className="group flex items-center justify-between gap-6 rounded-3xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] p-8 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-[#F4F2EC] flex items-center justify-center text-[#FF4E00]">
                  <Instagram className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="font-serif text-xl sm:text-2xl text-[#0D1117] group-hover:text-[#FF4E00] transition-colors">Follow the Journey</h2>
                  <p className="text-sm text-[#374151] mt-1">Race-day stories, behind-the-scenes and community shout-outs.</p>
                </div>
              </div>
              <ArrowUpRight className="w-6 h-6 text-[#FF4E00] shrink-0 transition-transform group-hover:rotate-45" />
            </motion.a>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="rounded-3xl bg-[#FF4E00] p-8 text-[#FBF9F5]"
            >
              <Heart className="w-6 h-6 mb-4" />
              <h2 className="font-serif text-xl sm:text-2xl">Every pace is welcome here.</h2>
              <p className="text-sm mt-2 opacity-90 leading-relaxed">
                First 5K or fiftieth marathon — the tribe runs together. Join a community event this month.
              </p>
              <Link
                to="/events"
                data-testid="community-events-link"
                className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#FBF9F5]/60 hover:bg-[#FBF9F5] hover:text-[#FF4E00] rounded-full px-6 py-3 transition-all duration-300"
              >
                See events <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          </div>
        </div>

        <div className="border-t border-[#0D1117]/10 pt-16" data-testid="finisher-wall">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="mb-10"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
              <Camera className="w-4 h-4" /> Finisher Wall
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#0D1117] tracking-tight max-w-2xl">
              Real finish-line moments from the tribe.
            </h2>
          </motion.div>
          {photos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {photos.map((p, i) => (
                <motion.figure
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: "easeOut" }}
                  className="group rounded-3xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-zoom-in"
                  data-testid="finisher-photo"
                  onClick={() => setLightbox(p)}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.url} alt={p.caption || "Finisher moment"} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  </div>
                  {p.caption && (
                    <figcaption className="p-5 text-sm text-[#374151]">{p.caption}</figcaption>
                  )}
                </motion.figure>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-[#FF4E00]/40 bg-[#FFFFFF] p-10 sm:p-14 text-center">
              <Camera className="w-8 h-8 text-[#FF4E00] mx-auto mb-5" />
              <p className="font-serif text-xl sm:text-2xl text-[#0D1117]">The wall is waiting for its first medal smile.</p>
              <p className="text-sm text-[#374151] mt-3 max-w-md mx-auto leading-relaxed">
                Finished a Fitygrity challenge or raced with the crew? Send us your finish-line photo and take your place on the wall.
              </p>
              <Link
                to="/contact"
                data-testid="finisher-wall-cta"
                className="mt-6 inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5] font-mono text-[11px] uppercase tracking-[0.2em] px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                Share your moment <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
      </main>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0D1117]/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setLightbox(null)}
            data-testid="finisher-lightbox"
          >
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-4xl w-full"
            >
              <button onClick={() => setLightbox(null)} className="absolute -top-12 right-0 text-[#FBF9F5]/80 hover:text-[#FF4E00] transition-colors" aria-label="Close" data-testid="lightbox-close">
                <X className="w-7 h-7" />
              </button>
              <img src={lightbox.url} alt={lightbox.caption || "Finisher moment"} className="w-full max-h-[75vh] object-contain rounded-2xl" />
              {lightbox.caption && (
                <figcaption className="mt-5 text-center font-serif text-lg sm:text-xl text-[#FBF9F5]">{lightbox.caption}</figcaption>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
      <Footer />
    </div>
  );
}
